import axios from 'axios'
import { createAsyncThunk } from '@reduxjs/toolkit'

const baseUrl = process.env.REACT_APP_MAP_SERVER_API_URL ?? ''
const companyId = process.env.REACT_APP_COMPANY_ID ?? '69d7925e89d8d58bc444ba89'

// Map the API's `type` field to one of the four map buckets. Matchers operate
// on the type lowercased with hyphens/spaces stripped:
//   'low-speed'    → 'lowspeed'    → speedLimitZones
//   'no-go'        → 'nogo'        → grayArea  (operational boundary)
//   'no-parking'   → 'noparking'   → noParkingZones
//   'parking'      → 'parking'     → parkingZones
//   'paid-parking' → 'paidparking' → parkingZones (own colour from BE)
const ZONE_TYPE_BUCKETS = [
  { bucket: 'parkingZones', match: ['parking', 'paidparking'] },
  { bucket: 'noParkingZones', match: ['noparking'] },
  { bucket: 'speedLimitZones', match: ['lowspeed', 'speedlimit', 'slow'] },
  { bucket: 'grayArea', match: ['nogo', 'grayarea', 'restricted', 'forbidden'] },
]

// Zone types we receive from the backend but deliberately don't render — these
// are internal/service zones (e.g. DUCK maintenance yards) that shouldn't be
// visible on the public map.
const HIDDEN_ZONE_TYPES = new Set(['maintenance'])

function bucketForType(zoneType) {
  const normalised = String(zoneType ?? '')
    .toLowerCase()
    .replace(/[-\s]/g, '')
  if (HIDDEN_ZONE_TYPES.has(normalised)) return null
  const hit = ZONE_TYPE_BUCKETS.find(b => b.match.includes(normalised))
  // Unknown types default to grayArea so they at least show up.
  return hit?.bucket ?? 'grayArea'
}

// Defensive item extraction — same trick as vehiclesAction.
function extractItems(payload) {
  if (Array.isArray(payload)) return payload
  if (Array.isArray(payload?.data?.items)) return payload.data.items
  if (Array.isArray(payload?.items)) return payload.items
  if (Array.isArray(payload?.data)) return payload.data
  return []
}

// API item shape: { color, description, geometry, label, type }
function zoneToFeature(zone) {
  // The backend doesn't expose a primary id for zones; derive a stable one from
  // type + label so the slice can detect "same zone, updated fields" vs
  // "different zone" across refreshes. Mapbox uses this via promoteId so the
  // layer can target individual features (feature-state etc.).
  const id = `${zone.type}:${zone.label}`
  return {
    type: 'Feature',
    geometry: zone.geometry,
    properties: {
      id,
      label: zone.label,
      description: zone.description,
      color: zone.color,
    },
  }
}

function emptyCollection() {
  return { type: 'FeatureCollection', features: [] }
}

export const getGeofencingZones = createAsyncThunk('geofencingZones/getGeofencingZones', async () => {
  try {
    // Public endpoint — no auth, no credentials.
    // baseUrl already includes the `/api` prefix (see .env), so the path here
    // starts at `/public/...` to avoid a `/api/api/` double-prefix.
    const response = await axios.get(`${baseUrl}/public/companies/${companyId}/zones`)
    const items = extractItems(response.data)

    const geofencingZonesFeatures = {
      grayArea: emptyCollection(),
      speedLimitZones: emptyCollection(),
      noParkingZones: emptyCollection(),
      parkingZones: emptyCollection(),
    }

    for (const zone of items) {
      const bucket = bucketForType(zone.type)
      if (!bucket) continue // HIDDEN_ZONE_TYPES (e.g. maintenance) — skip silently
      geofencingZonesFeatures[bucket].features.push(zoneToFeature(zone))
    }

    return geofencingZonesFeatures
  } catch (error) {
    console.warn('[getGeofencingZones] API call failed, returning empty zones:', error.message)
    return {
      grayArea: emptyCollection(),
      speedLimitZones: emptyCollection(),
      noParkingZones: emptyCollection(),
      parkingZones: emptyCollection(),
    }
  }
})
